import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Plus, Search, Package, Edit, Trash2, Filter, X,
  ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight,
  CheckCircle, XCircle, Tag, MoreHorizontal
} from 'lucide-react';
import { toast } from 'sonner';

export default function ProductsAndServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Selection & bulk
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    billing_type: '',
    is_active: '',
  });

  // Sorting
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 'service',
    price: '',
    billing_type: 'one-time',
    unit: 'each',
    sku: '',
    is_active: true
  });

  useEffect(() => { loadItems(); }, []);
  useEffect(() => { setCurrentPage(0); }, [searchTerm, filters, sortBy, sortOrder]);

  const loadItems = async () => {
    try {
      const response = await api.get('/products-services');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  // Unique categories from data
  const categories = useMemo(() => {
    const cats = new Set(items.map(i => i.category).filter(Boolean));
    return [...cats].sort();
  }, [items]);

  const billingTypes = useMemo(() => {
    const types = new Set(items.map(i => i.billing_type).filter(Boolean));
    return [...types].sort();
  }, [items]);

  // Filter, sort, paginate
  const processedItems = useMemo(() => {
    let filtered = [...items];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.name?.toLowerCase().includes(term) ||
        item.description?.toLowerCase().includes(term) ||
        item.sku?.toLowerCase().includes(term)
      );
    }
    if (filters.category) filtered = filtered.filter(i => i.category === filters.category);
    if (filters.billing_type) filtered = filtered.filter(i => i.billing_type === filters.billing_type);
    if (filters.is_active === 'true') filtered = filtered.filter(i => i.is_active !== false);
    if (filters.is_active === 'false') filtered = filtered.filter(i => i.is_active === false);

    filtered.sort((a, b) => {
      let aVal, bVal;
      switch (sortBy) {
        case 'name': aVal = (a.name || '').toLowerCase(); bVal = (b.name || '').toLowerCase(); break;
        case 'category': aVal = a.category || ''; bVal = b.category || ''; break;
        case 'price': aVal = parseFloat(a.price || a.unit_price || 0); bVal = parseFloat(b.price || b.unit_price || 0); break;
        case 'billing_type': aVal = a.billing_type || ''; bVal = b.billing_type || ''; break;
        case 'is_active': aVal = a.is_active ? 1 : 0; bVal = b.is_active ? 1 : 0; break;
        default: aVal = a.name || ''; bVal = b.name || '';
      }
      if (typeof aVal === 'number') return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      return sortOrder === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
    return filtered;
  }, [items, searchTerm, filters, sortBy, sortOrder]);

  const totalFiltered = processedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const paginatedItems = processedItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await api.put(`/products-services/${editingItem.id}`, formData);
        toast.success('Item updated successfully');
      } else {
        await api.post('/products-services', formData);
        toast.success('Item created successfully');
      }
      setDialogOpen(false);
      setEditingItem(null);
      resetForm();
      loadItems();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || '',
      category: item.category || 'service',
      price: item.price || item.unit_price || '',
      billing_type: item.billing_type || 'one-time',
      unit: item.unit || 'each',
      sku: item.sku || '',
      is_active: item.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    try {
      await api.delete(`/products-services/${id}`);
      toast.success('Item deleted');
      loadItems();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    // Select/deselect all items on the current page
    const currentPageIds = processedItems.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map(i => i.id);
    if (selectedIds.length === currentPageIds.length && currentPageIds.every(id => selectedIds.includes(id))) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentPageIds);
    }
  };

  const handleBulkAction = async (action, value) => {
    if (!selectedIds.length) return;
    try {
      const response = await api.post('/products-services/bulk', { ids: selectedIds, action, value });
      const r = response.data;
      toast.success(`${r.affected} item(s) updated`);
      setSelectedIds([]);
      setBulkAction('');
      setBulkValue('');
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk action failed');
    }
  };

  const handleBulkDelete = async () => {
    await handleBulkAction('delete');
    setDeleteConfirmOpen(false);
  };

  const resetForm = () => {
    setFormData({
      name: '', description: '', category: 'service', price: '',
      billing_type: 'one-time', unit: 'each', sku: '', is_active: true
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      service: 'bg-blue-100 text-blue-800',
      product: 'bg-purple-100 text-purple-800',
      software: 'bg-green-100 text-green-800',
      hardware: 'bg-orange-100 text-orange-800',
      recurring: 'bg-indigo-100 text-indigo-800',
      onetime: 'bg-neutral-100 text-neutral-800',
    };
    return colors[category] || 'bg-neutral-100 text-neutral-700';
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-neutral-400" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
  };

  const hasActiveFilters = filters.category || filters.billing_type || filters.is_active;

  const clearFilters = () => {
    setFilters({ category: '', billing_type: '', is_active: '' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Products & Services</h1>
          <p className="text-gray-600 mt-1">Manage your MSP offerings and pricing</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) { setEditingItem(null); resetForm(); }
        }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Product/Service
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Item' : 'Add Product/Service'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="service">Service</SelectItem>
                      <SelectItem value="product">Product</SelectItem>
                      <SelectItem value="software">Software</SelectItem>
                      <SelectItem value="hardware">Hardware</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Price *</Label>
                  <Input type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} required />
                </div>
                <div>
                  <Label>Billing Type</Label>
                  <Select value={formData.billing_type} onValueChange={(v) => setFormData({...formData, billing_type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="one-time">One-time</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Unit</Label>
                  <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="each">Each</SelectItem>
                      <SelectItem value="per user">Per User</SelectItem>
                      <SelectItem value="per device">Per Device</SelectItem>
                      <SelectItem value="per hour">Per Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>SKU</Label>
                  <Input value={formData.sku} onChange={(e) => setFormData({...formData, sku: e.target.value})} />
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={3} />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingItem ? 'Update' : 'Create'} Item</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="Search products and services..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="product-search"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}
          data-testid="product-filter-toggle"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <Badge className="ml-2 bg-white text-blue-700">{[filters.category, filters.billing_type, filters.is_active].filter(Boolean).length}</Badge>
          )}
        </Button>
        <div className="text-sm text-gray-600">{totalFiltered} items</div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Category</Label>
                <Select value={filters.category || undefined} onValueChange={(v) => setFilters({...filters, category: v || ''})}>
                  <SelectTrigger className="h-9" data-testid="filter-category">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(c => (
                      <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Billing Type</Label>
                <Select value={filters.billing_type || undefined} onValueChange={(v) => setFilters({...filters, billing_type: v || ''})}>
                  <SelectTrigger className="h-9" data-testid="filter-billing-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    {billingTypes.map(t => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Status</Label>
                <Select value={filters.is_active || undefined} onValueChange={(v) => setFilters({...filters, is_active: v || ''})}>
                  <SelectTrigger className="h-9" data-testid="filter-active">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Active</SelectItem>
                    <SelectItem value="false">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <Button size="sm" variant="outline" onClick={clearFilters} className="h-9">
                    <X className="h-3 w-3 mr-1" /> Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Products/Services Table */}
      <Card>
        <CardContent className="p-0">
          {/* Mobile Card View */}
          <div className="md:hidden p-3 space-y-2" data-testid="products-mobile-cards">
            {loading ? (
              <div className="text-center py-8 text-gray-500">Loading...</div>
            ) : paginatedItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No products or services found
              </div>
            ) : paginatedItems.map((item) => (
              <div
                key={item.id}
                className={`border rounded-lg p-3 bg-white ${selectedIds.includes(item.id) ? 'ring-2 ring-blue-400 bg-blue-50' : ''}`}
                data-testid={`product-card-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <Checkbox
                      checked={selectedIds.includes(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                      className="mt-1 shrink-0"
                    />
                    <div className="min-w-0">
                      <div className="font-semibold text-sm">{item.name}</div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="custom" className={getCategoryColor(item.category)}>{item.category || '-'}</Badge>
                        <Badge variant="custom" className={item.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {item.is_active !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      {item.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{item.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()}</p>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-semibold">${parseFloat(item.price || item.unit_price || 0).toFixed(2)}</div>
                    <div className="text-xs text-muted-foreground capitalize">{item.billing_type || '-'}</div>
                  </div>
                </div>
                <div className="flex justify-end gap-1 mt-2 pt-2 border-t">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(item)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </Button>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block">
          <Table data-testid="products-table" className="table-fixed w-full">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={selectedIds.length > 0 && paginatedItems.every(i => selectedIds.includes(i.id))}
                    onCheckedChange={toggleSelectAll}
                    data-testid="select-all-products"
                  />
                </TableHead>
                <TableHead className="cursor-pointer select-none w-[220px] resize-x overflow-hidden" onClick={() => handleSort('name')}>
                  <div className="flex items-center gap-1">Name <SortIcon field="name" /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none w-[120px] resize-x overflow-hidden" onClick={() => handleSort('category')}>
                  <div className="flex items-center gap-1">Category <SortIcon field="category" /></div>
                </TableHead>
                <TableHead className="w-[280px] resize-x overflow-hidden">Description</TableHead>
                <TableHead className="cursor-pointer select-none w-[100px] resize-x overflow-hidden" onClick={() => handleSort('price')}>
                  <div className="flex items-center gap-1">Price <SortIcon field="price" /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none w-[110px] resize-x overflow-hidden" onClick={() => handleSort('billing_type')}>
                  <div className="flex items-center gap-1">Billing <SortIcon field="billing_type" /></div>
                </TableHead>
                <TableHead className="cursor-pointer select-none w-[90px] resize-x overflow-hidden" onClick={() => handleSort('is_active')}>
                  <div className="flex items-center gap-1">Status <SortIcon field="is_active" /></div>
                </TableHead>
                <TableHead className="w-[90px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">Loading...</TableCell>
                </TableRow>
              ) : paginatedItems.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                    <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    No products or services found
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell>
                      <Checkbox
                        checked={selectedIds.includes(item.id)}
                        onCheckedChange={() => toggleSelect(item.id)}
                        data-testid={`select-product-${item.id}`}
                      />
                    </TableCell>
                    <TableCell className="font-semibold">{item.name}</TableCell>
                    <TableCell>
                      <Badge variant="custom" className={getCategoryColor(item.category)}>{item.category || '-'}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 max-w-[250px] truncate">
                      {item.description ? item.description.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 80) : '-'}
                    </TableCell>
                    <TableCell className="font-semibold">
                      ${parseFloat(item.price || item.unit_price || 0).toFixed(2)}
                      {item.unit && item.unit !== 'each' && <span className="text-xs text-gray-500 ml-1">/ {item.unit}</span>}
                    </TableCell>
                    <TableCell className="text-sm capitalize">{item.billing_type || '-'}</TableCell>
                    <TableCell>
                      <Badge variant="custom" className={item.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                        {item.is_active !== false ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} data-testid={`edit-product-${item.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </div>

          {/* Pagination */}
          {!loading && totalFiltered > 0 && (
            <div className="flex items-center justify-between p-4 border-t" data-testid="products-pagination">
              <div className="text-sm text-neutral-500">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalFiltered)} of {totalFiltered} items
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-neutral-500">Rows per page:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 10000 : parseInt(v)); setCurrentPage(0); }}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="product-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="10000">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(0)} className="h-8 px-2">First</Button>
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(p => Math.max(0, p - 1))} className="h-8 w-8 p-0">
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm text-neutral-600 px-2 min-w-[80px] text-center">Page {currentPage + 1} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} className="h-8 w-8 p-0">
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)} className="h-8 px-2">Last</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bulk Action Bar */}
      {selectedIds.length > 0 && (
        <Card className="sticky bottom-4 z-20 border-blue-200 bg-blue-50 shadow-lg" data-testid="bulk-action-bar">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-blue-800">
                {selectedIds.length} item(s) selected
              </span>
              <div className="flex items-center gap-2">
                {/* Set Active */}
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('set_active')} data-testid="bulk-set-active">
                  <CheckCircle className="h-3.5 w-3.5 mr-1 text-green-600" /> Set Active
                </Button>
                {/* Set Inactive */}
                <Button size="sm" variant="outline" onClick={() => handleBulkAction('set_inactive')} data-testid="bulk-set-inactive">
                  <XCircle className="h-3.5 w-3.5 mr-1 text-neutral-500" /> Set Inactive
                </Button>
                {/* Change Category */}
                <Select value="" onValueChange={(v) => handleBulkAction('set_category', v)}>
                  <SelectTrigger className="w-[150px] h-8" data-testid="bulk-set-category">
                    <Tag className="h-3.5 w-3.5 mr-1" />
                    <span className="text-xs">Set Category</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="software">Software</SelectItem>
                    <SelectItem value="hardware">Hardware</SelectItem>
                  </SelectContent>
                </Select>
                {/* Change Billing */}
                <Select value="" onValueChange={(v) => handleBulkAction('set_billing_type', v)}>
                  <SelectTrigger className="w-[150px] h-8" data-testid="bulk-set-billing">
                    <MoreHorizontal className="h-3.5 w-3.5 mr-1" />
                    <span className="text-xs">Set Billing</span>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="one-time">One-time</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="annually">Annually</SelectItem>
                  </SelectContent>
                </Select>
                {/* Delete */}
                <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteConfirmOpen(true)} data-testid="bulk-delete">
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                </Button>
                {/* Clear */}
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="h-8 px-2">
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.length} item(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the selected products/services. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleBulkDelete} className="bg-red-600 hover:bg-red-700">
              Delete {selectedIds.length} Items
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
